import { useState, useEffect } from 'react'
import { conversations, Conversation } from '../lib/api'

interface ConversationSidebarProps {
  ideaId?: string
  activeConversationId?: string
  onSelectConversation: (convo: Conversation) => void
  onCreateConversation: () => void
}

export default function ConversationSidebar({
  ideaId,
  activeConversationId,
  onSelectConversation,
  onCreateConversation,
}: ConversationSidebarProps) {
  const [convos, setConvos] = useState<Conversation[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')

  // Load conversations
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    conversations
      .list(ideaId)
      .then((data) => {
        if (!cancelled) setConvos(data || [])
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [ideaId])

  // Search filter
  const filtered = search.trim()
    ? convos.filter((c) =>
        c.title.toLowerCase().includes(search.toLowerCase())
      )
    : convos

  // Handle title edit
  const handleSaveTitle = async (convoId: string) => {
    if (!editTitle.trim()) return
    try {
      await conversations.updateTitle(convoId, editTitle.trim())
      setConvos((prev) =>
        prev.map((c) =>
          c.id === convoId ? { ...c, title: editTitle.trim() } : c
        )
      )
    } catch {
      // Ignore
    }
    setEditingId(null)
  }

  // Handle delete
  const handleDelete = async (convoId: string) => {
    try {
      await conversations.delete(convoId)
      setConvos((prev) => prev.filter((c) => c.id !== convoId))
    } catch {
      // Ignore
    }
  }

  const formatDate = (dateString: string) => {
    const d = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`
    return d.toLocaleDateString()
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b border-gray-800 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-300">
            Conversations
          </span>
          <button
            onClick={onCreateConversation}
            className="p-1 text-brand-400 hover:text-brand-300 hover:bg-gray-800 rounded transition"
            title="New conversation"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search conversations..."
          className="w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded text-xs text-gray-200 placeholder-gray-500 outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center">
            <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-4 text-center text-xs text-gray-500">
            {search ? 'No matching conversations' : 'No conversations yet'}
          </div>
        ) : (
          filtered.map((convo) => (
            <div
              key={convo.id}
              className={`group px-3 py-2.5 border-b border-gray-800/50 cursor-pointer transition hover:bg-gray-800/30 ${
                activeConversationId === convo.id ? 'bg-gray-800/50 border-l-2 border-l-brand-500' : ''
              }`}
              onClick={() => onSelectConversation(convo)}
            >
              {editingId === convo.id ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveTitle(convo.id)
                    if (e.key === 'Escape') setEditingId(null)
                  }}
                  onBlur={() => handleSaveTitle(convo.id)}
                  className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-xs text-gray-200 outline-none"
                  autoFocus
                />
              ) : (
                <>
                  <p className="text-xs font-medium text-gray-200 truncate">
                    {convo.title}
                  </p>
                  {convo.first_message_preview && (
                    <p className="text-[10px] text-gray-500 truncate mt-0.5">
                      {convo.first_message_preview}
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-gray-600">
                      {formatDate(convo.updated_at)}
                    </span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setEditingId(convo.id)
                          setEditTitle(convo.title)
                        }}
                        className="p-0.5 text-gray-500 hover:text-gray-300"
                        title="Edit title"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (confirm('Delete this conversation?')) {
                            handleDelete(convo.id)
                          }
                        }}
                        className="p-0.5 text-gray-500 hover:text-red-400"
                        title="Delete"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
